/**
 * datos.ts — §1 Datos Generales builder for pdfmake.
 *
 * Renders a 2-column boxed grid matching the shipped IE look (DAT-01):
 *   - Navy label cells (primary color bg, white bold text) — same width across all rows
 *   - White value cells with #c9d2dd bottom border
 *   - Pair rows: left-label / left-value / right-label / right-value (4 columns)
 *   - Full-width rows: label / value spanning columns 1-3 (colSpan=3)
 *
 * The background color is driven by layout.fillColor so pdfmake correctly
 * fills cells — fillColor on a bare cell definition outside a table is ignored.
 *
 * LABEL WIDTH: 35mm = 35 × 2.8346pt ≈ 99.21pt, uniform across all row types
 * so the navy label blocks share a consistent visual width.
 */
import type { Content, ContentTable, TableCell, CustomTableLayout } from "pdfmake/interfaces.js";
import { format, isValid, parseISO } from "date-fns";
import type { InformeVM } from "../types.js";
import { autoContrast, subtleBorder } from "./color.js";

/** 35mm converted to pdfmake pt (1mm = 2.8346pt). */
export const LABEL_WIDTH_PT = 35 * 2.8346;

/** Row types mirroring datosGeneralesLayout output (avoid dependency on web app). */
interface HalfCell {
  label: string;
  value: string;
}

interface PairRow {
  kind: "pair";
  left: HalfCell;
  right: HalfCell;
}

interface FullRow {
  kind: "full";
  label: string;
  value: string;
}

type GridRow = PairRow | FullRow;

function str(v: unknown): string {
  return v == null ? "" : String(v);
}

function fecha(v: unknown): string {
  const s = str(v);
  if (!s) return "";
  const d = parseISO(s);
  return isValid(d) ? format(d, "dd/MM/yyyy") : s;
}

/** Inline re-implementation of datosGeneralesLayout (same logic, no web dependency). */
function buildLayout(tipo: "IE" | "IS" | "AC", d: Record<string, unknown>): GridRow[] {
  const respLabel = tipo === "IE" ? "Evaluador" : tipo === "AC" ? "Responsable" : "Supervisor";
  const rows: GridRow[] = [];

  // Row 1: Cliente | N°
  rows.push({ kind: "pair", left: { label: "Cliente", value: str(d["cliente"]) }, right: { label: "N°", value: str(d["numero"]) } });

  // Row 2: Atención | Emisión (IE/IS) or Atención | Cargo (AC)
  if (tipo === "AC") {
    rows.push({ kind: "pair", left: { label: "Atención", value: str(d["atencion"]) }, right: { label: "Cargo", value: str(d["cargo"]) } });
  } else {
    rows.push({ kind: "pair", left: { label: "Atención", value: str(d["atencion"]) }, right: { label: "Emisión", value: fecha(d["emision"]) } });
  }

  // Responsable/Supervisor/Evaluador — full width for AC; paired with Cargo for IE/IS (FIX-1).
  if (tipo === "AC") {
    rows.push({ kind: "full", label: respLabel, value: str(d["supervisor"]) });
  } else {
    // IE and IS: show evaluador/supervisor next to the Cargo field so both are visible.
    rows.push({ kind: "pair", left: { label: respLabel, value: str(d["supervisor"]) }, right: { label: "Cargo", value: str(d["cargo"]) } });
  }

  // Anexo | Revisión
  rows.push({ kind: "pair", left: { label: "Anexo", value: str(d["anexo"]) }, right: { label: "Revisión", value: str(d["revision"]) } });

  // Fecha de inicio | Fecha de cierre
  rows.push({ kind: "pair", left: { label: "Fecha de inicio", value: fecha(d["fechaInicio"]) }, right: { label: "Fecha de cierre", value: fecha(d["fechaCierre"]) } });

  // Full-width rows: order differs by type
  if (tipo === "AC") {
    rows.push({ kind: "full", label: "Lugar", value: str(d["lugar"]) });
    rows.push({ kind: "full", label: "Referencia", value: str(d["referencia"]) });
  } else {
    rows.push({ kind: "full", label: "Referencia", value: str(d["referencia"]) });
    rows.push({ kind: "full", label: "Lugar", value: str(d["lugar"]) });
  }

  // OC | OT/OV (IS and AC only)
  if (tipo === "IS") {
    rows.push({ kind: "pair", left: { label: "OC (cliente)", value: str(d["ocCliente"]) }, right: { label: "OT/OV (MPSA)", value: str(d["otMpsa"]) } });
  } else if (tipo === "AC") {
    rows.push({ kind: "pair", left: { label: "OC (cliente)", value: str(d["ocCliente"]) }, right: { label: "OT/OV (marco)", value: str(d["otMpsa"]) } });
  }

  return rows;
}

/** Color constants */
const LABEL_BG = "#14385d"; // primary navy
const LABEL_TEXT = "#ffffff";
const VALUE_TEXT = "#333333";
const BORDER_COLOR = subtleBorder("#ffffff");

/** Label table cell (filled bg + contrasting bold text). */
function labelCell(text: string, color: string = LABEL_TEXT): TableCell {
  return {
    text,
    bold: true,
    fontSize: 8,
    color,
    // no border — layout handles all borders
  };
}

/** Value table cell (white bg + dark text). */
function valueCell(text: string): TableCell {
  return {
    text,
    fontSize: 9,
    color: VALUE_TEXT,
  };
}

/**
 * Build the §1 Datos Generales pdfmake Content block as a proper table.
 *
 * Columns: [labelLeft | valueLeft | labelRight | valueRight]
 * widths:  [LABEL_WIDTH_PT | * | LABEL_WIDTH_PT | *]
 *
 * layout.fillColor paints columns 0 and 2 navy and leaves 1 and 3 transparent.
 * For full-width rows, valueLeft has colSpan=3 and the remaining cells are empty.
 *
 * @param vm           - normalized InformeVM
 * @param labelBg      - optional override for the label background color (defaults to navy)
 */
export function datosGenerales(
  vm: InformeVM,
  labelBg = LABEL_BG,
): Content {
  // Merge datos + numero into a flat record for the layout helper.
  const d: Record<string, unknown> = { ...vm.datos };

  const gridRows = buildLayout(vm.tipo, d);

  // Label text must contrast against the label fill (white vanishes on a light primary).
  const labelText = autoContrast(labelBg);

  const body: TableCell[][] = gridRows.map((row) => {
    if (row.kind === "pair") {
      return [
        labelCell(row.left.label, labelText),
        valueCell(row.left.value),
        labelCell(row.right.label, labelText),
        valueCell(row.right.value),
      ];
    } else {
      // Full-width: label in col 0, value spans cols 1-3.
      const spanCell: TableCell = Object.assign(valueCell(row.value) as object, { colSpan: 3 }) as TableCell;
      return [
        labelCell(row.label, labelText),
        spanCell,
        {} as TableCell, // placeholder for colSpan
        {} as TableCell, // placeholder for colSpan
      ];
    }
  });

  const layout: CustomTableLayout = {
    // Full outer box + inner horizontal row separators (FIX-2).
    // i=0 = top border, i=body.length = bottom border — both included at 0.5pt.
    hLineWidth: () => 0.5,
    // Left (i=0) and right (i=numCols) outer verticals drawn; no inner verticals.
    vLineWidth: (i, node) => {
      const cols = (node.table.widths as unknown[]).length;
      return i === 0 || i === cols ? 0.5 : 0;
    },
    hLineColor: () => BORDER_COLOR,
    vLineColor: () => BORDER_COLOR,
    // Uniform cell padding matching the old renderer (6pt sides, 4pt top/bottom).
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 4,
    paddingBottom: () => 4,
    // Navy background on label columns (0 and 2); transparent on value columns.
    fillColor: (_row, _node, col) => {
      if (col === 0 || col === 2) return labelBg;
      return null;
    },
  };

  return {
    table: {
      widths: [LABEL_WIDTH_PT, "*", LABEL_WIDTH_PT, "*"],
      body,
      dontBreakRows: true,
    },
    layout,
    margin: [0, 0, 0, 8],
  } as unknown as Content;
}
